import L from "leaflet";
import { useCallback, useEffect, useRef, useState } from "react";
import Search, { Suggestions } from "../components/Search";
import MapboxAPI from "../services/MapboxAPI";
import { DRAWER_WIDTH } from "../utils/constants";
import { useAppConfigContext } from "./hooks/AppConfigContext";
import { useMap } from "./hooks/MapContext";
import { SearchForCityContext } from "./hooks/SearchContext";

const SearchForCityProvider = SearchForCityContext.Provider;

export const SearchForCityContainer = ({ children }) => {
  const appConfig = useAppConfigContext();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const cityBoundaries = useRef(null);
  const [sessionToken, setSessionToken] = useState("");
  const mapRef = useMap();

  useEffect(() => {
    if (mapRef && selectedCity?.bbox) {
      /** @type import('leaflet').Map */
      const map = mapRef;
      map.flyToBounds(selectedCity.bbox, { animate: true });

      setSelectedCity(null);
      setSuggestions([]);
      setSelectedSuggestion(null);
    }
  }, [selectedCity, mapRef]);

  useEffect(() => {
    if (selectedSuggestion) {
      setLoading(true);
      MapboxAPI.getCityDetails(selectedSuggestion.name)
        .then((resp) => {
          const feature = resp.data?.features?.[0];

          const bbox = feature?.properties?.extent;
          if (bbox?.length === 4) {
            setSelectedCity({
              geom: feature.geometry,
              bbox: [
                [bbox[1], bbox[0]],
                [bbox[3], bbox[2]],
              ],
            });
          }
        })
        .catch((err) => console.log(err))
        .finally(() => setLoading(false));
    }
  }, [appConfig, selectedSuggestion, sessionToken]);

  useEffect(() => {
    if (selectedCity?.geom && mapRef?.current) {
      if (cityBoundaries.current) {
        cityBoundaries.current.remove();
      }

      const bondariesLayer = L.geoJSON(selectedCity.geom, {
        style: function () {
          return {
            color: "blue",
            weight: 2,
            opacity: 0.7,
          };
        },
      }).addTo(mapRef.current);

      cityBoundaries.current = bondariesLayer;
    }
  }, [cityBoundaries, selectedCity, mapRef]);

  const fetchCityByText = useCallback(
    (text) => {
      setLoading(true);
      if (text?.length > 3) {
        const sessionToken = crypto.randomUUID();
        setSessionToken(sessionToken);

        return MapboxAPI.suggestCities(
          text,
          appConfig?.mapboxToken,
          sessionToken
        )
          .then((resp) => resp.data.suggestions)
          .catch(() => [])
          .finally(() => {
            setLoading(false);
          });
      } else {
        setLoading(false);
        if (cityBoundaries.current) {
          cityBoundaries.current.remove();
        }
        return Promise.resolve([]);
      }
    },
    [appConfig, cityBoundaries]
  );

  return (
    <SearchForCityProvider value={selectedCity}>
      <div
        style={{
          width: `calc(100% - ${window.innerWidth > 700 ? DRAWER_WIDTH : 0}px)`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          position: "fixed",
          zIndex: 1001,
          pointerEvents: "none",
          padding: 10,
        }}
      >
        <Search
          label={"Buscar por Município"}
          fetch={fetchCityByText}
          onResult={setSuggestions}
          style={{ width: "calc(100% - 20px)", maxWidth: 500 }}
        />
        <Suggestions
          data={suggestions}
          onSelect={setSelectedSuggestion}
          loading={loading}
          style={{ width: "calc(100% - 20px)", maxWidth: 500 }}
        />
      </div>
      {children}
    </SearchForCityProvider>
  );
};
